import { apiAction, apiActionNoData } from "@/lib/api/action"
import { apiClient } from "@/lib/api/client"
import { refreshAccessToken } from "@/lib/api/refresh"
import { apiUrl } from "@/lib/api/url"
import { newFrontendURL } from "@/lib/new-url"
import {
  emailWithCodeSchema,
  provideCodeSchema,
  requestLoginCodeSchema,
} from "@/lib/schemas/signIn"

import { BotTrapFields } from "@/hooks/use-bot-trap"
import { Config } from "@/types/nango"

type ReturnType = {
  user: { id: number; email: string; first_name: string; last_name: string }
}

export type AuthIntent = "login" | "signup"

async function fetchCurrentUser(): Promise<Response | null> {
  try {
    return await fetch(apiUrl("/api/user/user/"), { credentials: "include" })
  } catch {
    return null
  }
}

/**
 * Tell whether the cookies currently in the jar grant an authenticated session.
 *
 * A 401 does not mean logged out yet: the access token may simply have expired
 * while a valid refresh token is still available. The refresh cookie is httpOnly
 * so its presence can only be probed server-side, hence the unconditional
 * refresh attempt before concluding the session is dead.
 */
export async function hasWorkingSession(): Promise<boolean> {
  let res = await fetchCurrentUser()
  if (res?.status === 401 && (await refreshAccessToken())) {
    res = await fetchCurrentUser()
  }
  return res?.ok ?? false
}

/** Redirect into the app when the session works. Returns true when it redirected. */
export async function redirectIfCookies(
  config: Config,
  redirectToLoggedIn: boolean = true,
  redirectTo: string = "/",
): Promise<boolean> {
  if (typeof window === "undefined") return false
  if (!(await hasWorkingSession())) return false
  window.location.href = newFrontendURL(config, redirectTo, redirectToLoggedIn)
  return true
}

export async function requestLoginCode(
  email: string,
  lang: string,
  intent: AuthIntent,
  appId: string,
  botTrap: BotTrapFields,
) {
  return await apiAction(
    { email: email, ...botTrap },
    requestLoginCodeSchema,
    async (parsed): Promise<null> =>
      apiClient({
        url: apiUrl("/api/auth/code/request/"),
        method: "POST",
        data: parsed.data,
        params: { app_id: appId, lang: lang, intent: intent },
      }),
  )
}

export async function confirmLoginCode(
  email: string,
  code: string,
  appId: string,
) {
  return await apiAction(
    { code: code, email: email },
    emailWithCodeSchema,
    async (parsed): Promise<ReturnType> =>
      apiClient({
        url: apiUrl("/api/auth/code/confirm/"),
        method: "POST",
        data: parsed.data,
        params: { app_id: appId },
      }),
  )
}

export async function signInWithProvider(
  provider: string,
  intent: AuthIntent,
  appId: string,
  code: string | null = null,
) {
  const referer =
    typeof window !== "undefined"
      ? window.location.origin + window.location.pathname
      : ""

  if (code === null) {
    const providerUrl = await apiActionNoData(
      async (): Promise<{ redirect_url: string }> =>
        apiClient({
          url: apiUrl(`api/auth/${provider}/get_redirect_url/`),
          params: { callback_url: referer, app_id: appId, intent: intent },
          method: "GET",
        }),
    )
    if (providerUrl.data == null) {
      return { message: "An error occurs, please try again", data: null }
    }
    return { message: "redirect", data: providerUrl.data.redirect_url }
  }

  const res = await apiAction(
    { code: code },
    provideCodeSchema,
    async (parsed): Promise<ReturnType> =>
      apiClient({
        url: apiUrl(`api/auth/${provider}/`),
        params: { callback_url: referer, app_id: appId, intent: intent },
        method: "POST",
        data: parsed.data,
      }),
  )
  if (!res.data) return { message: res.message, data: null }
  return { message: "Success", data: res.data }
}
